import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export default function AdminProductApprovals() {
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState(null);
  const [partnerPrice, setPartnerPrice] = useState('');
  const [customerPrice, setCustomerPrice] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(() => api.get('/admin/product-approvals').then((r) => setProducts(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const open = (p) => { setSel(p); setPartnerPrice(p.proposed_partner_price ?? ''); setCustomerPrice(''); setNote(''); };

  const decide = async (decision) => {
    await api.post(`/admin/products/${sel.product_id}/decision`, {
      decision,
      partner_price: partnerPrice === '' ? null : parseFloat(partnerPrice),
      farmbox_customer_price: customerPrice === '' ? null : parseFloat(customerPrice),
      note,
    });
    toast.success(`Product ${decision}`); setSel(null); load();
  };

  const margin = () => { const c = parseFloat(customerPrice), p = parseFloat(partnerPrice); return (c && p) ? `$${(c - p).toFixed(2)} (${Math.round(((c - p) / c) * 100)}%)` : '—'; };

  return (
    <div data-testid="admin-product-approvals">
      <p className="font-manrope text-dark/60 mb-6">Products awaiting FarmBox review. Set the customer retail price and confirm the partner cost before approving.</p>
      <div className="bg-white rounded-2xl border border-forest/10 overflow-hidden">
        {products.length === 0 ? <p className="p-12 text-center font-manrope text-dark/50">No products awaiting approval.</p> : (
          <div className="divide-y divide-forest/10">
            {products.map((p) => (
              <button key={p.product_id} onClick={() => open(p)} className="w-full text-left p-5 flex flex-wrap items-center gap-4 hover:bg-cream/60" data-testid={`pending-product-${p.product_id}`}>
                <div className="flex-1 min-w-[200px]"><h3 className="font-playfair text-lg font-bold text-dark">{p.name}</h3><p className="font-manrope text-sm text-dark/60">{p.farm_name} · {p.category} · Proposed ${p.proposed_partner_price ?? '—'}/{p.unit || 'unit'}</p></div>
                <span className="badge-local">{p.approval_status}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="bg-cream max-w-lg">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-playfair text-2xl">{sel.name}</DialogTitle><DialogDescription>Set partner cost and customer price, then approve.</DialogDescription></DialogHeader>
              <div className="space-y-4 font-manrope text-sm">
                <p className="text-dark/70">{sel.farm_name} · {sel.category} · {sel.unit}</p>
                {sel.description && <p className="text-dark/70">{sel.description}</p>}
                <div className="grid grid-cols-2 gap-3 bg-white rounded-xl p-4">
                  <div><span className="text-dark/50 block">Farm retail</span><span className="font-semibold">${sel.farm_retail_price ?? '—'}</span></div>
                  <div><span className="text-dark/50 block">Max weekly</span><span className="font-semibold">{sel.max_weekly_quantity ?? '—'}</span></div>
                </div>
                <div><Label>FarmBox partner cost ($)</Label><Input type="number" className="bg-white border-forest/20" value={partnerPrice} onChange={(e) => setPartnerPrice(e.target.value)} data-testid="approve-partner-price" /></div>
                <div><Label>FarmBox customer retail price ($)</Label><Input type="number" className="bg-white border-forest/20" value={customerPrice} onChange={(e) => setCustomerPrice(e.target.value)} data-testid="approve-customer-price" /></div>
                <p className="text-dark/60">Estimated margin: <strong>{margin()}</strong></p>
                <div><Label>Note to farm</Label><Textarea className="bg-white border-forest/20" rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => decide('Approve')} className="btn-primary py-2 px-4 text-sm" data-testid="product-approve">Approve</button>
                  <button onClick={() => decide('RequestChanges')} className="btn-secondary py-2 px-4 text-sm" data-testid="product-changes">Request Changes</button>
                  <button onClick={() => decide('Decline')} className="py-2 px-4 text-sm rounded-full border-2 border-red-200 text-red-600 font-manrope font-bold hover:bg-red-50" data-testid="product-decline">Decline</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
